import type { Mission, Vehicle } from '@/state/types';
import { VEHICLE_SPECS } from './vehicles';

/**
 * Phase 7.1.6 — Internal validation of "Maßnahmen erfüllt".
 *
 * The mission's `requiredVehicles` is no longer shown in the disposition UI
 * (per user spec), but the rule engine still uses it after arrival to decide
 * whether to surface an "insufficient measures" warning to the player.
 *
 * This helper produces a human-readable summary describing exactly what's
 * missing — used for the on-scene warning toast.
 */

export interface RequirementCheckResult {
  met: boolean;
  /** Per-type breakdown: required count vs. assigned count among on-scene-eligible vehicles. */
  shortfall: { type: string; label: string; need: number; have: number }[];
  summary: string;
}

export function computeRequirementCheck(
  mission: Mission,
  vehicles: Vehicle[],
): RequirementCheckResult {
  // Count assigned vehicles per type (any status — they were dispatched).
  const assignedTypeCount = new Map<string, number>();
  for (const v of vehicles) {
    if (v.assignedMissionId !== mission.id) continue;
    assignedTypeCount.set(v.type, (assignedTypeCount.get(v.type) ?? 0) + 1);
  }

  const shortfall: RequirementCheckResult['shortfall'] = [];
  for (const r of mission.requiredVehicles) {
    const have = assignedTypeCount.get(r.type) ?? 0;
    if (have < r.count) {
      shortfall.push({
        type: r.type,
        label: VEHICLE_SPECS[r.type].label,
        need: r.count,
        have,
      });
    }
  }

  if (shortfall.length === 0) {
    return { met: true, shortfall, summary: 'Anforderungen erfüllt.' };
  }

  const parts = shortfall.map(
    (s) => `${s.label} ${s.have} / ${s.need}`,
  );
  return {
    met: false,
    shortfall,
    summary: `Fehlt: ${parts.join(', ')}`,
  };
}
